import { z } from 'zod'

const urlWithOptionalProtocol =
  /^(https?:\/\/)?((?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}(\/[^/\s]*)*$/

const required = z.string().min(1, { message: 'This field is required' })
const email = z.string().email({ message: 'Please enter a valid email address' })
const url = z.string().url({ message: 'Please enter a valid URL' })
const coercedString = z
  .string()
  .trim()
  .transform((val: string) => (val === '' ? undefined : val))
  .optional()
const requiredArray = required.array().nonempty()

const minLength = (min: number) =>
  z.string().min(min, {
    message: `Must be at least ${min} characters`,
  })

// Create reusable schemas
export const schemas = {
  // Basic validators
  required: required,
  requiredArray: requiredArray,
  requiredArrayOrString: z.union([requiredArray, required]),
  email: email,
  url: url,
  urlWithoutProtocol: z.string().refine((value: string) => urlWithOptionalProtocol.test(value), {
    message: 'Invalid URL format. Expected format like "example.com" or "sub.domain.co.uk/path"',
  }),

  // Numeric validators
  integer: z.coerce.number().int({ message: 'Must be an integer' }),
  positiveNumber: z.coerce.number().positive({ message: 'Must be a positive number' }),

  // String validators
  phone: z.string().regex(/^\(\d{3}\)\s\d{3}-\d{4}$/, {
    message: 'Please enter a valid phone number in the format (123) 456-7890',
  }),

  // Create parametrized validators
  minLength: minLength,
  maxLength: (max: number) =>
    z.string().max(max, {
      message: `Must not exceed ${max} characters`,
    }),
  exactLength: (length: number) =>
    z.string().length(length, {
      message: `Must be exactly ${length} characters`,
    }),

  // Optional validators (allows empty string)
  optional: coercedString.optional(),
  optionalPhone: coercedString.pipe(minLength(10).optional()),
  optionalEmail: coercedString.pipe(email.optional()),
  optionalUrl: coercedString.pipe(url.optional()),
}

// Helper to create required validators with custom messages
export const requiredWith = (message: string) => {
  return z.string().min(1, { message })
}

// Parse validation objects like { required: true, email: true }
export const parseValidationObject = (validationObj: Record<string, boolean>) => {
  if (!validationObj || Object.keys(validationObj).length === 0) {
    return z.any()
  }

  let schema = z.any()

  const { required = true, ...validations } = validationObj

  if (required) {
    schema = schema.pipe(schemas.required)
  } else {
    schema = schema.pipe(schemas.optional)
  }

  for (const [validation, value] of Object.entries(validations)) {
    // Use type assertion to tell TypeScript that validation is a valid key of schemas
    let valSchema = schemas[validation as keyof typeof schemas]

    if (valSchema && value) {
      // Check if valSchema is a function
      if (typeof valSchema === 'function') {
        valSchema = valSchema(value)
      }

      if (required) {
        schema = schema.pipe(valSchema)
      } else {
        schema = schema.pipe(valSchema.optional())
      }
    }
  }
  return schema
}
